import type { IntlShape } from "react-intl";

import type { PersonSummary } from "../components";
import type { SupportedCountry } from "../components/interface";
import { SignerSubRoleEnum } from "../types";

export interface ValidationResult {
  isValid: boolean;
  error?: {
    message: string;
    title: string;
  };
}

export interface SignerValidationByCountry {
  validate: (signers: PersonSummary[], intl: IntlShape) => ValidationResult;
}

const auValidation = (signers: PersonSummary[], intl: IntlShape): ValidationResult => {
  const canBeContractSigners = signers.filter(
    ({ signerSubRole }) => signerSubRole && !signerSubRole?.includes(SignerSubRoleEnum.NONE)
  );
  const contractSigners = canBeContractSigners.filter(({ isSignee }) => isSignee);
  const canBeContractSignersDirectors: PersonSummary[] = [];
  const canBeContractSignersSeparateSecretary: PersonSummary[] = [];

  const contractSignersDirectors: PersonSummary[] = [];
  const contractSignersPowerOfAttorney: PersonSummary[] = [];
  const contractSignersSeparateSecretary: PersonSummary[] = [];

  canBeContractSigners.forEach((canBeContractSigner) => {
    const { signerSubRole } = canBeContractSigner;

    if (signerSubRole && signerSubRole.includes(SignerSubRoleEnum.DIRECTOR)) {
      canBeContractSignersDirectors.push(canBeContractSigner);
    }
    if (
      signerSubRole &&
      signerSubRole.includes(SignerSubRoleEnum.SECRETARY) &&
      !signerSubRole.includes(SignerSubRoleEnum.DIRECTOR)
    ) {
      canBeContractSignersSeparateSecretary.push(canBeContractSigner);
    }
  });

  contractSigners.forEach((contractSigner) => {
    const { signerSubRole } = contractSigner;

    if (signerSubRole && signerSubRole.includes(SignerSubRoleEnum.DIRECTOR)) {
      contractSignersDirectors.push(contractSigner);
    }

    if (signerSubRole && signerSubRole.includes(SignerSubRoleEnum.POWER_OF_ATTORNEY)) {
      contractSignersPowerOfAttorney.push(contractSigner);
    }

    if (
      signerSubRole &&
      signerSubRole.includes(SignerSubRoleEnum.SECRETARY) &&
      !signerSubRole.includes(SignerSubRoleEnum.DIRECTOR)
    ) {
      contractSignersSeparateSecretary.push(contractSigner);
    }
  });

  // If there is not added enough people with the authority
  if (!canBeContractSigners.length) {
    return createValidationError(
      intl.formatMessage({ id: "Contract signer required", defaultMessage: "Contract signer required" }),
      intl.formatMessage({
        id: "You need to add people with the authority to do so",
        defaultMessage: "You need to add people with the authority to do so",
      })
    );
  }
  // If the contract signer is not selected
  if (canBeContractSigners.length && !contractSigners.length) {
    return createValidationError(
      intl.formatMessage({ id: "Contract signer required", defaultMessage: "Contract signer required" }),
      intl.formatMessage({
        id: "You need to select a contract signer",
        defaultMessage: "You need to select a contract signer",
      })
    );
  }

  // If one director is selected as a contract signer
  if (contractSignersDirectors.length === 1 && contractSigners.length === 1 && canBeContractSigners.length > 1) {
    // If there is another director added, need to select an additional director
    if (canBeContractSignersDirectors.length > 1) {
      return createValidationError(
        intl.formatMessage({
          id: "You need to select an additional director",
          defaultMessage: "You need to select an additional director",
        }),
        intl.formatMessage({
          id: "As you have multiple directors, you must select at least two of them to sign the contract.",
          defaultMessage: "As you have multiple directors, you must select at least two of them to sign the contract.",
        })
      );
    }
    // If there is separate secretary added, one secretary must be selected
    if (contractSignersSeparateSecretary.length === 0 && canBeContractSignersSeparateSecretary.length) {
      return createValidationError(
        intl.formatMessage({ id: "You need to select a secretary", defaultMessage: "You need to select a secretary" }),
        intl.formatMessage({
          id: "As you have a separate secretary, you must choose a secretary to sign the contract.",
          defaultMessage: "As you have a separate secretary, you must choose a secretary to sign the contract.",
        })
      );
    }
  }
  // If only one director and no more directors or company secretaries
  if (
    contractSignersDirectors.length === 1 &&
    !contractSignersPowerOfAttorney.length &&
    canBeContractSignersDirectors.length === 1 &&
    !canBeContractSignersSeparateSecretary.length &&
    contractSignersDirectors[0] &&
    !contractSignersDirectors[0].isSingleDirectorConfirmed
  ) {
    return createValidationError(
      intl.formatMessage({
        id: "You must confirm that there are no more directors or company secretaries",
        defaultMessage: "You must confirm that there are no more directors or company secretaries",
      }),
      intl.formatMessage({
        id: "As you selected one director, you must confirm that there are no more directors or company secretaries, or you will need to add details of another director or a secretary as they will also need to sign the contract.",
        defaultMessage:
          "As you selected one director, you must confirm that there are no more directors or company secretaries, or you will need to add details of another director or a secretary as they will also need to sign the contract.",
      })
    );
  }
  // If one secretary is selected as a contract signer need to select at least one director
  if (contractSignersSeparateSecretary.length && contractSignersDirectors.length === 0) {
    return createValidationError(
      intl.formatMessage({
        id: "You also need to select a director",
        defaultMessage: "You also need to select a director",
      }),
      intl.formatMessage({
        id: "As you have chosen a secretary, you must also select at least one director.",
        defaultMessage: "As you have chosen a secretary, you must also select at least one director.",
      })
    );
  }
  // If directors and individuals with power of attorney are selected
  if (contractSignersDirectors.length && contractSignersPowerOfAttorney.length) {
    return createValidationError(
      intl.formatMessage({
        id: "The signer must be a director or an individual with power of attorney but can't be both",
        defaultMessage: "The signer must be a director or an individual with power of attorney but can't be both",
      }),
      intl.formatMessage({
        id: "As you have selected a director, you can't select an individual with power of attorney.",
        defaultMessage: "As you have selected a director, you can't select an individual with power of attorney.",
      })
    );
  }

  // All checks passed
  return createValidationSuccess();
};

const createValidationError = (title: string, message: string): ValidationResult => {
  return {
    isValid: false,
    error: {
      title,
      message,
    },
  };
};

const createValidationSuccess = (): ValidationResult => {
  return {
    isValid: true,
  };
};

export const signerValidationByCountry: Partial<Record<SupportedCountry, SignerValidationByCountry>> = {
  AU: {
    validate: auValidation,
  },
};
